import React from 'react';

/**
 * Avatar component that displays user profile picture or initials fallback
 * @param {Object} user - User object with name and picture
 * @param {string} size - Size: 'sm' (32px), 'md' (48px), 'lg' (96px), 'xl' (128px)
 * @param {string} className - Additional CSS classes
 * @param {Function} onClick - Optional click handler
 */
function Avatar({ user, size = 'md', className = '', onClick, testId }) {
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const sizeClasses = {
    xs: 'w-6 h-6 text-[10px]',
    sm: 'w-8 h-8 text-xs',
    md: 'w-12 h-12 text-sm',
    lg: 'w-24 h-24 text-2xl',
    xl: 'w-32 h-32 text-4xl'
  };

  const sizeClass = sizeClasses[size] || sizeClasses.md;
  const initials = getInitials(user?.name || '');

  // Generate a consistent color based on user name
  const getColorFromName = (name) => {
    if (!name) return '#6B7280'; // gray-500
    const colors = [
      '#EF4444', // red
      '#F59E0B', // amber
      '#10B981', // emerald
      '#3B82F6', // blue
      '#8B5CF6', // violet
      '#EC4899', // pink
      '#14B8A6', // teal
      '#F97316', // orange
    ];
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const bgColor = getColorFromName(user?.name);

  if (user?.picture) {
    return (
      <img
        src={user.picture}
        alt={user.name || 'User'}
        className={`${sizeClass} rounded-full border border-white/10 object-cover ${onClick ? 'cursor-pointer hover:border-white/40 transition-all' : ''} ${className}`}
        onClick={onClick}
        data-testid={testId}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} rounded-full border border-white/10 flex items-center justify-center font-semibold text-white ${onClick ? 'cursor-pointer hover:border-white/40 transition-all' : ''} ${className}`}
      style={{ backgroundColor: bgColor }}
      onClick={onClick}
      data-testid={testId}
    >
      {initials}
    </div>
  );
}

export default Avatar;
